// Guest session in the `area` room (M1-15 lite): join, clock sync, remote
// replay, own prediction, chat. If no server answers, the game stays offline (M0).
import { Client } from '@colyseus/sdk';
import { C2S, eidFromKey, eidKey, PROTOCOL_VERSION, S2C } from '@enugu/shared';
import { ClockSync } from './clock';
import { EMOTE_NAME, EMOTE_WIRE } from './chat';
import { OwnPredictor, readMover, REMOTE_DELAY_MS, remoteSmoother, sameRoute } from './movers';

export const DEFAULT_SERVER_PORT = 2567;
const JOIN_TIMEOUT_MS = 5000;

/** `?server=` (ws URL or host:port) else same host, port 2567. */
export function resolveServerUrl(loc, param) {
    const wsProto = loc.protocol === 'https:' ? 'wss:' : 'ws:';
    if (param) return /^wss?:\/\//.test(param) ? param : `${wsProto}//${param}`;
    return `${wsProto}//${loc.hostname}:${DEFAULT_SERVER_PORT}`;
}

const withTimeout = (p, ms) => new Promise((resolve, reject) => {
    const id = setTimeout(() => reject(new Error(`timeout after ${ms} ms`)), ms);
    p.then((v) => { clearTimeout(id); resolve(v); }, (e) => { clearTimeout(id); reject(e); });
});

export class NetSession {
    // opts: { scene, player, bridge, chat, outfitFor(eid), onStatus(status) }
    constructor(opts) {
        Object.assign(this, opts);
        this.status = 'offline';
        this.room = null;
        this.clock = new ClockSync();
        this.myEid = null;
        this.remotes = new Map(); // key -> { ch, mover: last wire mover, name }
        this.own = null;
        this.cmid = 1;
        this.pingTimer = null;
        this.remoteNow = () => this.clock.now() - REMOTE_DELAY_MS;
        this.serverNow = () => this.clock.now();
    }
    get online() {
        return this.status === 'online';
    }
    setStatus(s) {
        if (s === this.status) return;
        this.status = s;
        this.bridge.me.value = { eid: this.myEid, name: 'you', online: s === 'online', net: s };
        this.onStatus?.(s);
    }
    async connect(url) {
        if (this.status === 'connecting' || this.room) return;
        this.setStatus('connecting');
        const client = new Client(url);
        const joining = client.joinOrCreate('area', { pv: PROTOCOL_VERSION });
        let room;
        try {
            room = await withTimeout(joining, JOIN_TIMEOUT_MS);
        } catch (err) {
            joining.then((r) => r.leave(), () => {});
            console.info(`[net] offline (${url}): ${err?.message ?? err}`);
            this.setStatus('offline');
            return;
        }
        this.attach(room);
    }
    attach(room) {
        this.room = room;
        this.own = new OwnPredictor(this.player.mover, this.serverNow);
        room.onMessage(S2C.Welcome, (m) => {
            this.myEid = m.eid;
            this.clock.seed(m.roomMs);
            this.setStatus('online');
            this.bridge.me.value = { eid: m.eid, name: 'you', online: true, net: 'online' };
            this.sync();
        });
        room.onMessage(S2C.Pong, (m) => this.clock.onPong(m));
        room.onMessage(S2C.MoveRejected, (m) => this.own?.onRejected(m));
        room.onMessage(S2C.Said, (m) => this.onSaid(m));
        room.onMessage(S2C.Emoted, (m) => this.onEmoted(m));
        room.onMessage(S2C.Error, (m) => console.warn('[net] server error', m));
        room.onMessage('*', () => {}); // other S2C ids are handled by later tasks
        room.onStateChange(() => this.sync());
        room.onDrop(() => this.setStatus('reconnecting'));
        room.onReconnect(() => this.setStatus('online'));
        room.onLeave((code) => this.detach(code));
        this.startPings();
    }
    startPings() {
        let n = 0;
        const ping = () => {
            this.room?.send(C2S.Ping, { c: performance.now() });
            n++;
            this.pingTimer = setTimeout(ping, n < 4 ? 500 : 3000);
        };
        ping();
    }
    detach(code) {
        clearTimeout(this.pingTimer);
        for (const r of this.remotes.values()) {
            this.chat.dropCharacter(r.ch);
            this.scene.removeCharacter(r.ch);
        }
        this.remotes.clear();
        if (this.player.mover.timed) this.player.mover.clearTimed();
        this.room = null;
        this.own = null;
        this.myEid = null;
        console.info(`[net] left the room (code ${code}), offline`);
        this.setStatus('offline');
    }
    // Mirrors state.players into scene characters (works with reflected, untyped state).
    sync() {
        const players = this.room?.state?.players;
        if (!players || this.myEid === null) return;
        const myKey = eidKey(this.myEid);
        const seen = new Set();
        players.forEach((p, key) => {
            if (!p?.mover) return;
            seen.add(key);
            const wire = readMover(p.mover);
            if (key === myKey) {
                this.own.onServer(wire);
                return;
            }
            let r = this.remotes.get(key);
            if (!r) {
                const eid = eidFromKey(key);
                const ch = this.scene.addCharacter({ x: wire.x, y: wire.y, name: p.name || `player ${key}`, outfit: this.outfitFor(eid) });
                ch.mover.resetTimed(wire, this.remoteNow);
                ch.mover.smooth = remoteSmoother();
                r = { ch, eid, mover: wire, name: p.name || 'someone' };
                this.remotes.set(key, r);
                return;
            }
            r.name = p.name || r.name;
            if (!sameRoute(wire, r.mover) || wire.startAt !== r.mover.startAt) {
                r.ch.mover.setTimed(wire, this.remoteNow);
                r.mover = wire;
            }
        });
        for (const [key, r] of this.remotes) {
            if (seen.has(key)) continue;
            this.chat.dropCharacter(r.ch);
            this.scene.removeCharacter(r.ch);
            this.remotes.delete(key);
        }
    }
    /** Online tap-to-move. Returns false when offline (caller walks locally). */
    moveTo(goal) {
        if (!this.online || !this.own?.joined) return false;
        const plan = this.own.moveTo(goal, this.scene.astar);
        if (plan) this.room.send(plan.type === 'move' ? C2S.Move : C2S.Stop, plan.msg);
        return true;
    }
    byEid(e) {
        if (e === this.myEid) return { ch: this.player, name: 'you' };
        const r = this.remotes.get(eidKey(e));
        return r ? { ch: r.ch, name: r.name } : { ch: null, name: 'someone' };
    }
    // Bridge commands: show locally at once, send when online.
    handleCommand(cmd) {
        const me = this.player;
        if (cmd.type === 'say') {
            const cmid = this.cmid++;
            this.chat.said(me, 'you', cmd.text, this.myEid);
            if (this.online) this.room.send(C2S.Say, { text: cmd.text, cmid });
        } else if (cmd.type === 'chip') {
            this.chat.chip(me, 'you', cmd.id, this.myEid);
            if (this.online) this.room.send(C2S.Emote, { chip: cmd.id });
        } else if (cmd.type === 'emote') {
            this.chat.emoted(me, 'you', cmd.id, this.myEid);
            const id = EMOTE_WIRE[cmd.id];
            if (this.online && id) this.room.send(C2S.Emote, { emote: id });
        }
    }
    onSaid(m) {
        if (m.kind === 1) {
            this.chat.system(m.text);
            return;
        }
        if (m.e === this.myEid) return; // shown locally when sent
        const { ch, name } = this.byEid(m.e);
        this.chat.said(ch, name, m.text, m.e);
    }
    onEmoted(m) {
        if (m.e === this.myEid) return; // shown locally when sent
        const { ch, name } = this.byEid(m.e);
        if (m.chip) this.chat.chip(ch, name, m.chip, m.e);
        else if (m.emote !== undefined) this.chat.emoted(ch, name, EMOTE_NAME[m.emote] ?? 'wave', m.e);
    }
}
